import { JobStatus } from 'components/StatusTag/StatusTag';
import { JobUtilizationMetrics } from 'types/insights';

export type vendorTypes =
  | 'Kubernetes'
  | 'Databricks'
  | 'GCP Cloud Dataproc'
  | 'Azure HDInsight';

export type SparkJobStatuses =
  | JobStatus.Succeeded
  | JobStatus.Cancelled
  | JobStatus.Failed
  | JobStatus.In_Queue
  | JobStatus.Timed_Out
  | JobStatus.Running;

export enum MVLanguageEnum {
  'Spark Python' = 'Spark Python',
  'Spark SQL/PostgreSQL' = 'Spark SQL/PostgreSQL',
  'Spark R' = 'Spark R',
  'Spark Scala' = 'Spark Scala',
}

export type JobsListResponse = {
  dtos: JobDTO[];
  totalDuration: number;
  numberOfJobs: number;
  currentPage: number;
};

export type JobDTO = {
  sparkJobId: string;
  createdAt: string;
  queueingTime: number;
  duration: number;
  schemaName: string;
  tableName: string;
  sparkJobStatus: SparkJobStatuses;
  mvLanguage: MVLanguageEnum;
  totalExecutorCount: number;
  // Server-side average over Arcane k8s.pod.memory_request_utilization for this job.
  // May be omitted when Arcane is unavailable or no numeric series exists.
  efficiency?: number;
};

export type JobStateResult = {
  stateId: number;
  stateResult: string;
};

export type DetailedSparkJobDTO = JobDTO & {
  key: string;
  clusterName: string;
  clusterUrl: string;
  clusterPrefix: string;
  vendor: string;
  updatedAt: string;
  dequeuedAt: string;
  sparkAppId: string;
  sparkAppName: string;
  mvLanguage: MVLanguageEnum;
  incortaGuid: string;
  totalExecutorCount: number;
  peakMemoryInMB: number;
  sparkDriverMemoryInMB: number;
  code: string;
  properties: string;
  jobState: string;
  extractedRowCount: number;
  rejectedRowCount: number;
  loadedRowCount: number;
  jobStates: JobStateResult[];
  // Container metrics (MAX / aggregated from Arcane) on job detail. Omitted if unavailable.
  arcaneMetrics?: JobUtilizationMetrics;
};

export type ChartResposnse = {
  Jobs: DetailedSparkJobDTO[];
  TotalJobs: number;
};

export type SparkDriverMemoryResponse = {
  TimeData: string[];
  MemoryUsed: number[];
  MemoryLimits: number[];
};

export type DiskMetrics = {
  TimeData: string[];
  Usage: number[];
  Capacity: number[];
};

export type JobMemoryPair = {
  JobID: string;
  ReservedMemory: number;
};

export type ReservedMemoryDetail = {
  JobMemoryPairs: JobMemoryPair[];
};

export type LogBasedMetricsResponse = {
  TimeData: string[];
  Points: number[];
  MemoryDetails: ReservedMemoryDetail[];
};

export enum pathKeys {
  ROOT = 'ROOT',
  SIGN_IN = 'SIGN_IN',
  JOB_DETAILS = 'JOB_DETAILS',
  JOB_INFO = 'JOB_INFO',
  ALL_CLUSTERS = 'ALL_CLUSTERS',
  ALL_CLUSTERS_SIGN_IN = 'ALL_CLUSTERS_SIGN_IN',
  ALL_CLUSTERS_TABLE = 'ALL_CLUSTERS_TABLE',
  ALL_CLUSTERS_JOB_INFO = 'ALL_CLUSTERS_JOB_INFO',
  METRICS = 'METRICS',
  CHIDORI_CONNECT = 'CHIDORI_CONNECT',
  TABLE_DETAILS = 'TABLE_DETAILS',
  OVERVIEW = 'OVERVIEW',
  JOBS = 'JOBS',
}

export const AVAILABLE_PATHS = new Map([
  [pathKeys.ALL_CLUSTERS, '/'],
  [pathKeys.ALL_CLUSTERS_SIGN_IN, '/signin'],
  [pathKeys.ROOT, '/cluster/:clusterName'],
  [pathKeys.CHIDORI_CONNECT, '/cluster/:clusterName/chidoriConnect'],
  [pathKeys.SIGN_IN, '/cluster/:clusterName/signin'],
  [pathKeys.JOB_DETAILS, '/cluster/:clusterName/jobDetails/:jobId'],
  [pathKeys.TABLE_DETAILS, '/cluster/:clusterName/tableDetails/:tableName'],
  [pathKeys.JOB_INFO, '/cluster/:clusterName/jobinfo/:jobId'],
  [pathKeys.METRICS, '/cluster/:clusterName/metrics'],
  // Overview shares the cluster index path; Jobs moves under /jobs.
  [pathKeys.OVERVIEW, '/cluster/:clusterName'],
  [pathKeys.JOBS, '/cluster/:clusterName/jobs'],
]);

export enum menuItemKey {
  OVERVIEW = 'overview',
  JOBS = 'jobs',
  METRICS = 'metrics',
  CHIDORI_CONNECT = 'connect',
}

export type StatusesCounts = {
  all: number;
  Succeeded: number;
  Cancelled: number;
  Failed: number;
  In_Queue: number;
  Running: number;
};

export enum filtersKeys {
  JOB_ID = 'JOB_ID',
  CREATED_AT_DATE_RANGE = 'CREATED_AT_DATE_RANGE',
  STATUS = 'STATUS',
  MV = 'MV',
  SCHEMA = 'SCHEMA',
}

export type AppliedFilter = Partial<
  | Record<filtersKeys.JOB_ID, string>
  | Record<filtersKeys.STATUS, JobStatus[]>
  | Record<
      filtersKeys.CREATED_AT_DATE_RANGE,
      { start_date?: string; end_date?: string }
    >
  | Record<filtersKeys.MV, string[]>
  | Record<filtersKeys.SCHEMA, string[]>
>;

export const seachableTablePageSizeOptions = [10, 20, 30, 40, 50];

export enum ColumnSortEnum {
  'ascend' = 'ascend',
  'descend' = 'descend',
}

export type SelectedRange = {
  fromDate: number;
  toDate: number;
};

export type ColumnSort = ColumnSortEnum.ascend | ColumnSortEnum.descend | null;

export const JobStepState: Record<number, string> = {
  0: 'NOT_RUNNING',
  1: 'RUNNING',
  2: 'SUCCESS',
  [-1]: 'FAILURE',
  3: 'PARTIAL_SUCCESS',
  4: 'SKIPPED',
};

export const JobStep: Record<number, string> = {
  10: 'In Queue',
  25: 'Allocate Resources',
  30: 'Enrichment',
  35: 'Deduplication',
  40: 'Load',
  50: 'Post-Load',
};

export const JobState: Record<number, string> = {
  100: 'SUCCESS',
  101: 'FAILED',
  102: 'FINISHED_WITH_ERRORS',
  103: 'INTERRUPTED',
  104: 'ABORTED',
  105: 'PARTIAL_SUCCESS',
  106: 'SKIPPED',
};
